import { BufferGeometry, Float32BufferAttribute, type BufferAttribute, type InterleavedBufferAttribute } from 'three';

/** Clip triangles against shared X planes before bending them. Keeps UV seams
 * and material groups; caps must not span a whole lens as one flat chord.
 *
 * A110Y: rewritten on flat typed arrays. The first version built, for every
 * vertex and every clipped polygon, small JavaScript arrays (`flatMap`,
 * `Array.from`, `map`): millions of short-lived allocations per frame, 0.4–1 s
 * of the 1–2 s procedural build on a server CPU (2–6 s on a phone), with heap
 * peaks of +50 MB. Same output, triangle for triangle.
 */
export function subdivideForWrap(geometry: BufferGeometry, step = 3): void {
  if (!(step > 0)) throw new Error('Wrap subdivision step must be positive');
  const names = Object.keys(geometry.attributes);
  const attrs = names.map(name => geometry.getAttribute(name) as BufferAttribute | InterleavedBufferAttribute);
  const sizes = attrs.map(a => a.itemSize);
  const offsets: number[] = [];
  let stride = 0;
  for (const size of sizes) { offsets.push(stride); stride += size; }
  const p = offsets[names.indexOf('position')]!;
  const index = geometry.getIndex();
  const positionCount = geometry.getAttribute('position').count;
  const count = index?.count ?? positionCount;

  // Interleaved copy of the input vertices (one read per vertex instead of one per use).
  const input = new Float64Array(positionCount * stride);
  for (let j = 0; j < attrs.length; j++) {
    const a = attrs[j]!;
    const size = sizes[j]!;
    const off = offsets[j]!;
    for (let id = 0; id < positionCount; id++) {
      const base = id * stride + off;
      for (let k = 0; k < size; k++) input[base + k] = a.getComponent(id, k);
    }
  }

  // Output, interleaved, grown by doubling; one Float32 attribute per name at the end.
  let out = new Float32Array(Math.max(4096, count * stride * 2));
  let outLen = 0;
  const emitVertex = (src: Float64Array, off: number): void => {
    if (outLen + stride > out.length) {
      const bigger = new Float32Array(out.length * 2);
      bigger.set(out);
      out = bigger;
    }
    for (let k = 0; k < stride; k++) out[outLen + k] = src[off + k]!;
    outLen += stride;
  };

  // Scratch polygons: a triangle clipped by two planes has at most 5 vertices (8 for safety).
  const MAX_POLY = 8;
  const tri = new Float64Array(3 * stride);
  const polyA = new Float64Array(MAX_POLY * stride);
  const polyB = new Float64Array(MAX_POLY * stride);

  /** Sutherland–Hodgman against the plane x = `x`, keeping the side `above` (x ≥) or below (x ≤). */
  const clip = (src: Float64Array, n: number, dst: Float64Array, x: number, above: boolean): number => {
    let m = 0;
    for (let i = 0; i < n; i++) {
      const ai = i * stride;
      const bi = ((i + 1) % n) * stride;
      const da = src[ai + p]! - x;
      const db = src[bi + p]! - x;
      const insideA = above ? da >= 0 : da <= 0;
      const insideB = above ? db >= 0 : db <= 0;
      if (insideA) {
        const di = m * stride;
        for (let k = 0; k < stride; k++) dst[di + k] = src[ai + k]!;
        m++;
      }
      if (insideA !== insideB) {
        const t = da / (da - db);
        const di = m * stride;
        for (let k = 0; k < stride; k++) {
          const value = src[ai + k]!;
          dst[di + k] = value + (src[bi + k]! - value) * t;
        }
        dst[di + p] = x;
        m++;
      }
    }
    return m;
  };

  /** Fan-triangulates `poly` and emits the triangles, skipping the degenerate ones. */
  const emitPolygon = (poly: Float64Array, n: number): void => {
    for (let j = 1; j + 1 < n; j++) {
      const a = 0;
      const b = j * stride;
      const c = (j + 1) * stride;
      const ux = poly[b + p]! - poly[a + p]!, uy = poly[b + p + 1]! - poly[a + p + 1]!, uz = poly[b + p + 2]! - poly[a + p + 2]!;
      const vx = poly[c + p]! - poly[a + p]!, vy = poly[c + p + 1]! - poly[a + p + 1]!, vz = poly[c + p + 2]! - poly[a + p + 2]!;
      // Clipping may leave a sub-pixel sliver on a flat cap. Its XY area
      // has no visible surface, but front wrap bends its collinear points
      // into a backwards-facing fin (notably at Muse's lens shoulder).
      if (Math.abs(uz) < 1e-6 && Math.abs(vz) < 1e-6 && Math.abs(ux * vy - uy * vx) < 1e-3) continue;
      if (Math.hypot(uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx) < 1e-7) continue;
      emitVertex(poly, a);
      emitVertex(poly, b);
      emitVertex(poly, c);
      emitted += 3;
    }
  };

  const groups = geometry.groups.length ? [...geometry.groups] : [{ start: 0, count, materialIndex: 0 }];
  geometry.clearGroups();
  let emitted = 0;
  for (const group of groups) {
    const start = emitted;
    for (let i = group.start; i < group.start + group.count; i += 3) {
      for (let j = 0; j < 3; j++) {
        const id = index ? index.getX(i + j) : i + j;
        const base = id * stride;
        const dst = j * stride;
        for (let k = 0; k < stride; k++) tri[dst + k] = input[base + k]!;
      }
      // Ear clipping may leave numerically collinear cap triangles. Bending
      // one would turn its negligible flat area into a spurious vertical fin.
      const ax = tri[p]!, ay = tri[p + 1]!, az = tri[p + 2]!;
      const bx = tri[stride + p]!, by = tri[stride + p + 1]!, bz = tri[stride + p + 2]!;
      const cx = tri[2 * stride + p]!, cy = tri[2 * stride + p + 1]!, cz = tri[2 * stride + p + 2]!;
      const ux = bx - ax, uy = by - ay, uz = bz - az;
      const vx = cx - ax, vy = cy - ay, vz = cz - az;
      if (Math.hypot(uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx) < 1e-5) continue;
      const minX = Math.min(ax, bx, cx);
      const maxX = Math.max(ax, bx, cx);
      const first = Math.floor(minX / step);
      const last = Math.max(first, Math.ceil(maxX / step) - 1);
      for (let strip = first; strip <= last; strip++) {
        const n1 = clip(tri, 3, polyA, strip * step, true);
        const n2 = clip(polyA, n1, polyB, (strip + 1) * step, false);
        emitPolygon(polyB, n2);
      }
    }
    geometry.addGroup(start, emitted - start, group.materialIndex);
  }
  geometry.setIndex(null);
  const vertices = outLen / stride;
  for (let j = 0; j < names.length; j++) {
    const size = sizes[j]!;
    const off = offsets[j]!;
    const array = new Float32Array(vertices * size);
    for (let v = 0, src = off, dst = 0; v < vertices; v++, src += stride, dst += size) {
      for (let k = 0; k < size; k++) array[dst + k] = out[src + k]!;
    }
    geometry.setAttribute(names[j]!, new Float32BufferAttribute(array, size));
  }
}
